import React, { useState } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Divider, Snackbar, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons for Document Portal
import DescriptionIcon from '@mui/icons-material/Description';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BadgeIcon from '@mui/icons-material/Badge';

// Import our new split components
import DocumentStatusTab from './DocumentStatusTab';
import DocumentVaultTab from './DocumentVaultTab';
import ScreenedDocumentsTab from './ScreenedDocumentsTab';

const AdminDocumentPortal = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const [activeMenu, setActiveMenu] = useState('submissions');
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  // UPDATED: NavItem with icon and theme-based styling
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => setActiveMenu(id)} 
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
          '&.Mui-selected .MuiListItemText-primary': { 
            color: 'white',
            fontWeight: 'bold',
          },
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeMenu === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  // Section Header with theme-based styling
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop,
      gap: 0
    }}>
      
      {/* LEFT NAVIGATION PANEL - Matching Dashboard style */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <SectionHeader title="DOCUMENT STATUS" />
          <NavItem id="submissions" label="Submissions" icon={<DescriptionIcon fontSize="small" />} />
          <NavItem id="pendings" label="Pendings" icon={<PendingActionsIcon fontSize="small" />} />
          
          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />
          
          <SectionHeader title="DOCUMENT VAULT" />
          <NavItem id="pending_approval" label="Pending Approval" icon={<FolderIcon fontSize="small" />} />
          <NavItem id="history" label="Approval History" icon={<HistoryIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="SCREENED DOCUMENTS" />
          <NavItem id="pan_card" label="PAN Card" icon={<CreditCardIcon fontSize="small" />} />
          <NavItem id="aadhaar_card" label="Aadhaar Card" icon={<BadgeIcon fontSize="small" />} />
          
        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        bgcolor: '#fbfbfb',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        width: '100%',
      }}>
        {/* Header with title */}
        <Box sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e0e0e0', 
          bgcolor: '#fbfbfb',
          flexShrink: 0,
        }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.9rem' }}>
            {activeMenu.replace('_', ' ')}
          </Typography>
        </Box>
        
        {/* Content area with independent scrolling */}
        <Box sx={{ 
          flexGrow: 1, 
          width: '100%', 
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 2,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        }}>
          {(activeMenu === 'submissions' || activeMenu === 'pendings') && <DocumentStatusTab activeMenu={activeMenu} showMsg={showMsg} />}
          {(activeMenu === 'pending_approval' || activeMenu === 'history') && <DocumentVaultTab activeMenu={activeMenu} showMsg={showMsg} />}
          {(activeMenu === 'pan_card' || activeMenu === 'aadhaar_card') && <ScreenedDocumentsTab activeMenu={activeMenu} showMsg={showMsg} />}
        </Box>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AdminDocumentPortal;